import React, { forwardRef, ReactNode, useEffect, useRef, useState } from 'react';
import { clsx } from 'clsx';
import { useLazyQuery } from '@apollo/client';
import { AdacHeadline, AdacLoadingSpinner, AdacText } from '@adac-de/component-library';
import { logger } from '@adac-de/e-charge-common';
import { NavigationType, useLocation, useNavigationType } from 'react-router-dom';
import { SystemCapacityWizardContent } from './sections/system-capacity/system-capacity';
import { SunKilometersWizardContent } from './sections/sun-kilometers/sun-kilometers';
import { SolarYieldWizardContent } from './sections/solar-yield/solar-yield';
import styles from './wizard.module.scss';

import { SectionKey, UpdateAnswers } from 'types';
import { SolarStorageWizardContent } from 'sections/solar-storage/solar-storage';
import pvWizardResultGql from 'queries/pv-wizard-result.gql';
import { ResultWizardContent } from 'sections/result/result';
import { Stepper } from '@components/stepper/stepper';
import { useNavigateWithoutButton } from '@hooks/use-navigate-without-button';
import { useIsBreakpoint } from '@hooks/use-is-breakpoint';
import { Breakpoints } from '@utils/breakpoints';
import { HeaderHeights } from '@utils/header-heights';
import { useSearchParams } from '@hooks/use-search-params';
import { useWizard } from '@hooks/use-wizard';
import { wizardReducer } from '@hooks/use-wizard-reducer';
import { mapSearchParamsToWizardType } from '@hooks/utils/map-search-params-to-wizard-type';
import { simplifyAnswers } from '@hooks/utils/simplify-answers';
import { WizardActionType, WizardState } from '@hooks/utils/wizard-types';

type WizardProps = {
  headline: string;
  sectionData: PvWizardSections;
  toggleDisclaimerModal: () => void;
  modalContainerRef: React.RefObject<HTMLDivElement>;
};

type WizardContentProps = {
  hideItemTop?: boolean;
  itemTop: ReactNode;
  itemBottom: ReactNode;
  className?: string;
  classNameItemTop?: string;
  classNameItemBottom?: string;
};

export const WizardContent = forwardRef<HTMLDivElement, WizardContentProps>(
  (
    {
      classNameItemTop,
      classNameItemBottom,
      className,
      hideItemTop = false,
      itemTop,
      itemBottom,
      ...rest
    },
    ref
  ) => (
    <div className={clsx(styles.content, className)} ref={ref} {...rest}>
      <div
        className={clsx(
          styles.wizardItem,
          classNameItemTop,
          hideItemTop && styles.wizardHideItemTop
        )}
      >
        {itemTop}
      </div>
      <div className={clsx(styles.wizardItem, classNameItemBottom)}>{itemBottom}</div>
    </div>
  )
);

let isInitialLoading = true;

export const Wizard: React.FC<WizardProps> = ({
  headline,
  sectionData,
  toggleDisclaimerModal,
  modalContainerRef
}) => {
  const [isLoading, setIsLoading] = useState(true);
  const navigationType = useNavigationType();
  const isBackNavigation = navigationType === NavigationType.Pop;

  const [resultsState, setResultsState] = useState<PvWizardResult>();
  const outerRef = useRef<HTMLDivElement>(null);
  const headlineRef = useRef<HTMLDivElement>(null);
  const isDesktop = useIsBreakpoint(Breakpoints.M);

  const location = useLocation();

  const sectionOrder = Object.keys(sectionData).filter(
    data => data !== '__typename' && data !== 'offerSection'
  ) as SectionKey[];

  const steps = sectionOrder.map(sectionKey => ({
    topLine: sectionData[sectionKey].topLine,
    key: sectionKey
  }));

  const [searchParams, setSearchParams] = useSearchParams(sectionOrder, sectionData);
  const mappedSearchParams = mapSearchParamsToWizardType(searchParams);
  const [wizardState, dispatch] = useWizard(
    wizardReducer,
    sectionData,
    sectionOrder,
    mappedSearchParams
  );

  const activeIndex = steps.findIndex(s => s.key === wizardState.activeWizardStep);

  const [getWizardResults, { data: results, error: wizardResultsError }] = useLazyQuery<
    PvWizardResultsQuery,
    PvWizardResultsQueryVariables
  >(pvWizardResultGql, {
    variables: wizardState.answers
  });

  useEffect(() => {
    // to prevent flickering on selection at first slide
    if (!searchParams.entries.length) {
      isInitialLoading = false;
    }
  }, []);

  useEffect(() => {
    if (Boolean(Object.keys(wizardState.answers).length)) {
      setIsLoading(true);
      getWizardResults({
        variables: wizardState.answers
      }).finally(() => {
        setIsLoading(false);
        isInitialLoading = false;
      });
    }
  }, [wizardState]);

  useEffect(() => {
    setIsLoading(false);
  }, []);

  // browser back navigation only updates view when using location not being connected to either searchParams or wizardState
  useEffect(() => {
    if (isBackNavigation) {
      const updatedAnswers = mapSearchParamsToWizardType(new URLSearchParams(location.search));
      const isWizardStateEqualSearchState =
        JSON.stringify(wizardState.answers) === JSON.stringify(updatedAnswers);

      if (!isWizardStateEqualSearchState) {
        dispatch({
          type: WizardActionType.RESTORE_ANSWERS,
          payload: {
            updatedAnswer: updatedAnswers
          }
        });
      }
    }

    setIsLoading(false);
  }, [location]);

  useEffect(() => {
    if (results) {
      setResultsState(results.pvWizardResult);
    }
  }, [results]);

  const updateAnswer: UpdateAnswers = updatedAnswer => {
    dispatch({
      type: WizardActionType.UPDATE_ANSWER,
      payload: { updatedAnswer }
    });
  };

  if (wizardResultsError) {
    logger.error(`Error while fetching wizard results: ${wizardResultsError}`);
  }

  const scrollIntoView = () => {
    if (headlineRef.current && outerRef.current) {
      const elementPosition = isDesktop
        ? headlineRef.current.getBoundingClientRect().top
        : outerRef.current.getBoundingClientRect().top;
      const FAHRZEUGWELT_HEADER_HEIGHT = isDesktop ? HeaderHeights.Desktop : HeaderHeights.Mobile;
      const offsetPosition = elementPosition + window.scrollY - FAHRZEUGWELT_HEADER_HEIGHT;

      setTimeout(() => {
        window.scroll({ behavior: 'smooth', top: offsetPosition });
      }, 0);
    }
  };

  const updateWizard = (isWizardClickedThrough?: boolean) => {
    scrollIntoView();
    dispatch({ type: WizardActionType.NEXT_QUESTION, isWizardClickedThrough });
  };

  const updateSearchParams = (state: WizardState) => {
    const simplifiedAnswers = simplifyAnswers(state.answers);
    setSearchParams(new URLSearchParams(simplifiedAnswers));
  };

  const showNextQuestion = (state: WizardState, isWizardClickedThrough?: boolean) => {
    updateSearchParams(state);
    updateWizard(isWizardClickedThrough);
  };

  const navigateWithoutButton = useNavigateWithoutButton({
    wizardState,
    updateWizard,
    updateParams: updateSearchParams
  });

  if (isLoading && isInitialLoading) {
    return <AdacLoadingSpinner icon="Car" />;
  }

  return (
    <div ref={headlineRef}>
      {!wizardState.isWizardClickedThrough && (
        <AdacHeadline styleAs="h2" tagName="h2" center>
          {headline}
        </AdacHeadline>
      )}

      <div className="margin-top--s" ref={outerRef}>
        {!wizardState.isWizardClickedThrough && (
          <>
            <Stepper steps={steps} activeIndex={activeIndex} resultsState={resultsState} />

            {wizardResultsError && (
              <AdacText color="highlightRed">
                Fehler beim Aktualiseren der Ergebnisse. Bitte versuchen Sie es später erneut.
              </AdacText>
            )}

            {wizardState.activeWizardStep === 'systemCapacity' && (
              <SystemCapacityWizardContent
                questions={sectionData.systemCapacity.questions}
                onAnswerUpdated={updateAnswer}
                nextStep={() => showNextQuestion(wizardState)}
                previousStep={() => {
                  scrollIntoView();
                  dispatch({ type: WizardActionType.PREV_QUESTION });
                }}
                wizardState={wizardState}
                result={resultsState?.systemCapacity}
                sectionKey={sectionData.systemCapacity.key}
                sectionInfo={sectionData.systemCapacity.info}
              />
            )}

            {wizardState.activeWizardStep === 'solarYield' && (
              <SolarYieldWizardContent
                questions={sectionData.solarYield.questions}
                onAnswerUpdated={updateAnswer}
                nextStep={() => showNextQuestion(wizardState)}
                previousStep={() => {
                  scrollIntoView();
                  dispatch({ type: WizardActionType.PREV_QUESTION });
                }}
                wizardState={wizardState}
                result={resultsState?.solarYield}
                sectionKey={sectionData.solarYield.key}
                sectionInfo={sectionData.solarYield.info}
              />
            )}

            {wizardState.activeWizardStep === 'sunKilometers' && (
              <SunKilometersWizardContent
                questions={sectionData.sunKilometers.questions}
                onAnswerUpdated={updateAnswer}
                nextStep={() => showNextQuestion(wizardState)}
                previousStep={() => {
                  scrollIntoView();
                  dispatch({ type: WizardActionType.PREV_QUESTION });
                }}
                wizardState={wizardState}
                result={resultsState?.sunKilometers}
                sectionKey={sectionData.sunKilometers.key}
                sectionInfo={sectionData.sunKilometers.info}
                showNextButton={!navigateWithoutButton}
              />
            )}
            {wizardState.activeWizardStep === 'solarStorage' && (
              <SolarStorageWizardContent
                questions={sectionData.solarStorage.questions}
                onAnswerUpdated={updateAnswer}
                nextStep={() => {
                  showNextQuestion(
                    wizardState,
                    Boolean(wizardState.isLastQuestion && wizardState.isLastStep)
                  );
                }}
                previousStep={() => {
                  scrollIntoView();
                  dispatch({ type: WizardActionType.PREV_QUESTION });
                }}
                wizardState={wizardState}
                result={resultsState?.solarStorage}
                sectionKey={sectionData.solarStorage.key}
                sectionInfo={sectionData.solarStorage.info}
                modalRef={modalContainerRef}
                formModals={{
                  roofAge: sectionData.offerSection.formModals.roofAgeModal,
                  roofMaterial: sectionData.offerSection.formModals.roofMaterialModal
                }}
              />
            )}
            <AdacText
              onClick={toggleDisclaimerModal}
              className={styles.disclaimerText}
              type="small"
            >
              Nähere Informationen zur ADAC Musterrechnung
            </AdacText>
          </>
        )}

        {wizardState.isWizardClickedThrough && results && (
          <ResultWizardContent
            previousStep={() => {
              scrollIntoView();
            }}
            headline={sectionData.offerSection.headline}
            benefits={sectionData.offerSection.benefits}
            partners={sectionData.offerSection.partners}
            formHeadlines={sectionData.offerSection.formHeadlines}
            formModals={sectionData.offerSection.formModals}
            selectedPartnersContent={sectionData.offerSection.selectedPartnersContent}
            faqs={sectionData.offerSection.faqs}
            resetWizard={() => {
              scrollIntoView();
              dispatch({
                type: WizardActionType.RESET_WIZARD
              });
              setSearchParams(new URLSearchParams());
            }}
            result={resultsState?.result}
            toggleDisclaimerModal={toggleDisclaimerModal}
            modalContainerRef={modalContainerRef}
            scrollIntoView={() => scrollIntoView()}
            dispatch={dispatch}
            wizardState={wizardState}
          />
        )}
      </div>
    </div>
  );
};
